// DAEMON — records, splits and lifetime stats.
//
// The speedrunning layer. `replay.ts` owns the *proof* of a run (input
// streams, ghosts, run codes); this owns the *bookkeeping*: personal
// bests per category, per-phase splits, gold segments, sum-of-best, and
// the lifetime counters that let a player see themselves improving.
//
// Everything here is local. There is no leaderboard and none is wanted:
// a client-side score is a devtools edit away, and pretending otherwise
// would be theatre. What makes a time *credible* is the replay that
// reproduces it — which is `replay.ts`'s job, and why a run code carries
// an input hash rather than a number. This module is your own logbook.
//
// Pure except for two thin localStorage wrappers at the bottom, so the
// whole thing is testable by injecting a Storage.

import { TICK_HZ, type SimState } from "./types";

export const RECORDS_KEY = "daemon.records.v1";
export const RECORDS_VERSION = 1;

// ── Categories ────────────────────────────────────────────────────────
//
// Derived from the run, never chosen up front. You do not declare a
// no-hit attempt; you either got hit or you didn't. Declaring in advance
// only creates a reason to quit early, which is the opposite of what a
// category should do.

export type CategoryId = "any" | "no-hit" | "sigkill" | "all-phases" | "score";

export type CategoryDef = {
  id: CategoryId;
  name: string;
  blurb: string;
  /** Does a finished run qualify? */
  qualifies: (r: RunResult) => boolean;
  /**
   * How the board sorts this category.
   *
   * Every category but one is a speedrun and ranks on time ascending.
   * `score` ranks on points descending, and the two want opposite
   * things out of the same decision — spend PRIV to clear a card early,
   * or hoard it and stay in the bullets while the card times out. That
   * tension is the reason to have both rather than fold them into one
   * number nobody could read.
   */
  rankBy: "ticks" | "score";
};

export const CATEGORIES: CategoryDef[] = [
  {
    id: "any",
    rankBy: "ticks" as const,
    name: "any%",
    blurb: "Terminate DAEMON. Nothing else matters.",
    qualifies: (r) => r.won,
  },
  {
    id: "no-hit",
    rankBy: "ticks" as const,
    name: "no-hit",
    blurb: "Win without being touched. Legible to a spectator in three seconds.",
    qualifies: (r) => r.won && r.hitsTaken === 0,
  },
  {
    id: "sigkill",
    rankBy: "ticks" as const,
    name: "SIGKILL%",
    blurb: "Win having drafted nothing. No upgrades, no excuses.",
    qualifies: (r) => r.won && r.upgrades.length === 0,
  },
  {
    id: "all-phases",
    rankBy: "ticks" as const,
    name: "all phases",
    blurb: "Win having cleared every card rather than timing any out.",
    qualifies: (r) => r.won && r.cardsCleared >= r.cardsTotal && r.cardsTotal > 0,
  },
  {
    id: "score",
    rankBy: "score" as const,
    name: "load average",
    blurb:
      "Points, not time. Graze to raise your load; it bleeds away the moment you play safe, and a single hit collapses it.",
    // Losing runs count. Unlike every other board here, a score run that
    // ends in death is still a legitimate score — it is the arcade
    // reading, and refusing it would mean the only way to post is to
    // win, which quietly makes this a speedrun category again.
    qualifies: (r) => r.score > 0,
  },
];

export const categoryById = (id: CategoryId): CategoryDef | undefined =>
  CATEGORIES.find((c) => c.id === id);

/** Every category a finished run qualifies for. A single good run can
 *  set four personal bests at once, which is exactly the intent. */
export function qualifyingCategories(r: RunResult): CategoryId[] {
  return CATEGORIES.filter((c) => c.qualifies(r)).map((c) => c.id);
}

// ── Splits ────────────────────────────────────────────────────────────

/** One segment of a run. `at` is the cumulative tick it ended on;
 *  `duration` is the segment itself — the number a gold is measured on. */
export type Split = {
  key: string;
  name: string;
  at: number;
  duration: number;
};

/**
 * Accumulates splits during a run.
 *
 * Segments are measured from the previous split, not from the start, so
 * a bad opening cannot poison every subsequent gold. That is the whole
 * point of segment timing and it is why sum-of-best is a meaningful
 * (if unreachable) target rather than a curiosity.
 */
export function splitsFrom(marks: Array<{ key: string; name: string; at: number }>): Split[] {
  let prev = 0;
  return marks.map((m) => {
    const duration = Math.max(0, m.at - prev);
    prev = m.at;
    return { key: m.key, name: m.name, at: m.at, duration };
  });
}

// ── A finished run ────────────────────────────────────────────────────

export type RunResult = {
  seed: string;
  nice: number;
  daily: boolean;
  won: boolean;
  ticks: number;
  hitsTaken: number;
  grazes: number;
  /** Points. Earned by sustained danger — see the load-average block in
   *  sim.ts. Present on losing runs too, which is the one board here
   *  that accepts them. */
  score: number;
  /** Highest load reached, in thousandths. The run's peak nerve, kept
   *  because the final load says nothing: a hit on the last card would
   *  erase an otherwise flawless run's whole story. */
  peakLoadMilli: number;
  damageDealt: number;
  signalsFired: number;
  cardsCleared: number;
  cardsTotal: number;
  zombiesReaped: number;
  upgrades: string[];
  /** Upgrades offered across the whole run, for pick-rate stats. */
  offered: string[];
  /** The card that was on screen when it ended. */
  lastCard: string | null;
  /** Deepest phase index reached, 0-based. Carried explicitly rather
   *  than inferred from `splits.length`: there is one split per *card*
   *  and several cards per phase, so counting splits over-reports depth
   *  by roughly a factor of two. */
  deepestPhase: number;
  splits: Split[];
};

/** Build a RunResult from the sim's final state. Keeps the shape of the
 *  record in one place rather than at every call site. */
export function resultFromState(
  s: SimState,
  extra: { won: boolean; splits: Split[]; offered: string[]; lastCard: string | null },
): RunResult {
  return {
    seed: s.config.seed,
    nice: s.config.nice,
    daily: s.config.daily,
    won: extra.won,
    ticks: s.tick,
    hitsTaken: s.stats.hitsTaken,
    grazes: s.stats.grazes,
    score: s.stats.score,
    peakLoadMilli: s.stats.peakLoadMilli,
    damageDealt: s.stats.damageDealt,
    signalsFired: s.stats.signalsFired,
    cardsCleared: s.stats.cardsCleared,
    cardsTotal: s.cardPlan.reduce((n, set) => n + set.length, 0),
    zombiesReaped: s.stats.zombiesReaped,
    upgrades: [...s.upgrades],
    offered: extra.offered,
    lastCard: extra.lastCard,
    deepestPhase: s.phaseIndex,
    splits: extra.splits,
  };
}

// ── The stored shape ──────────────────────────────────────────────────

export type PersonalBest = {
  ticks: number;
  seed: string;
  nice: number;
  daily: boolean;
  at: number;
  splits: Split[];
};

export type CardStat = {
  seen: number;
  cleared: number;
  deaths: number;
  /** Fastest clear of this card as a segment, in ticks. */
  best: number | null;
};

export type UpgradeStat = {
  offered: number;
  taken: number;
  wins: number;
};

export type Lifetime = {
  runs: number;
  wins: number;
  losses: number;
  quits: number;
  ticks: number;
  grazes: number;
  hitsTaken: number;
  damageDealt: number;
  signalsFired: number;
  cardsCleared: number;
  zombiesReaped: number;
  /** Deepest runlevel index reached, ever. */
  deepestPhase: number;
  /** Best (lowest) nice level ever beaten. Null until a first win. */
  hardestWonNice: number | null;
};

export type DailyEntry = { day: string; ticks: number; won: boolean };

export type Records = {
  version: number;
  /** Personal bests, keyed `${category}:${nice}`. Difficulty is part of
   *  the key because a time at nice 19 and one at nice -20 are not
   *  comparable and merging them would make both meaningless. */
  pbs: Record<string, PersonalBest>;
  /** Best-ever duration for each individual segment — the "golds". */
  golds: Record<string, number>;
  cards: Record<string, CardStat>;
  upgrades: Record<string, UpgradeStat>;
  lifetime: Lifetime;
  /** Newest first, capped. */
  daily: DailyEntry[];
  /** Newest first, capped — feeds the recent-runs list. */
  recent: Array<{ ticks: number; won: boolean; nice: number; at: number; seed: string }>;
};

const MAX_DAILY = 60;
const MAX_RECENT = 25;

export const pbKey = (category: CategoryId, nice: number): string => `${category}:${nice}`;

export function emptyRecords(): Records {
  return {
    version: RECORDS_VERSION,
    pbs: {},
    golds: {},
    cards: {},
    upgrades: {},
    lifetime: {
      runs: 0,
      wins: 0,
      losses: 0,
      quits: 0,
      ticks: 0,
      grazes: 0,
      hitsTaken: 0,
      damageDealt: 0,
      signalsFired: 0,
      cardsCleared: 0,
      zombiesReaped: 0,
      deepestPhase: 0,
      hardestWonNice: null,
    },
    daily: [],
    recent: [],
  };
}

// ── Applying a run ────────────────────────────────────────────────────

export type Improvement = {
  /** Categories in which this run set a new personal best. */
  personalBests: CategoryId[];
  /** Split keys whose segment beat the previous gold. */
  golds: string[];
  /** Ticks saved against the previous any% PB, if there was one. */
  savedVsPb: number | null;
};

/**
 * Fold a finished run into the records. Pure: returns new records and a
 * description of what improved, so the results screen can celebrate the
 * right thing without recomputing any of it.
 */
export function applyRun(
  prev: Records,
  run: RunResult,
  opts: { outcome: "won" | "lost" | "quit"; day?: string; now?: number } = { outcome: "lost" },
): { records: Records; improvement: Improvement } {
  const now = opts.now ?? 0;
  const r: Records = {
    ...prev,
    pbs: { ...prev.pbs },
    golds: { ...prev.golds },
    cards: { ...prev.cards },
    upgrades: { ...prev.upgrades },
    lifetime: { ...prev.lifetime },
    daily: [...prev.daily],
    recent: [...prev.recent],
  };
  const improvement: Improvement = { personalBests: [], golds: [], savedVsPb: null };

  // Lifetime. A quit still counts as a run — pretending otherwise would
  // flatter the win rate, and the point of these numbers is to be true.
  const lt = r.lifetime;
  lt.runs += 1;
  if (opts.outcome === "won") lt.wins += 1;
  else if (opts.outcome === "lost") lt.losses += 1;
  else lt.quits += 1;
  lt.ticks += run.ticks;
  lt.grazes += run.grazes;
  lt.hitsTaken += run.hitsTaken;
  lt.damageDealt += run.damageDealt;
  lt.signalsFired += run.signalsFired;
  lt.cardsCleared += run.cardsCleared;
  lt.zombiesReaped += run.zombiesReaped;
  lt.deepestPhase = Math.max(lt.deepestPhase, run.deepestPhase);
  if (run.won && (lt.hardestWonNice === null || run.nice < lt.hardestWonNice)) {
    lt.hardestWonNice = run.nice;
  }

  // Cards: seen, cleared, and which one ended the run.
  for (const sp of run.splits) {
    const c = r.cards[sp.key] ?? { seen: 0, cleared: 0, deaths: 0, best: null };
    r.cards[sp.key] = {
      seen: c.seen + 1,
      cleared: c.cleared + 1,
      deaths: c.deaths,
      best: c.best === null ? sp.duration : Math.min(c.best, sp.duration),
    };
  }
  if (!run.won && run.lastCard) {
    const c = r.cards[run.lastCard] ?? { seen: 0, cleared: 0, deaths: 0, best: null };
    r.cards[run.lastCard] = { ...c, seen: c.seen + 1, deaths: c.deaths + 1 };
  }

  // Upgrades: offered vs taken is the only way to spot a dead card.
  for (const id of run.offered) {
    const u = r.upgrades[id] ?? { offered: 0, taken: 0, wins: 0 };
    r.upgrades[id] = { ...u, offered: u.offered + 1 };
  }
  for (const id of run.upgrades) {
    const u = r.upgrades[id] ?? { offered: 0, taken: 0, wins: 0 };
    r.upgrades[id] = { ...u, taken: u.taken + 1, wins: u.wins + (run.won ? 1 : 0) };
  }

  // Golds are tracked on every run, won or lost. A segment you nailed in
  // a run you later threw away is still the fastest you have ever done
  // it, and refusing to count it is how sum-of-best becomes a lie.
  for (const sp of run.splits) {
    const best = r.golds[sp.key];
    if (best === undefined || sp.duration < best) {
      r.golds[sp.key] = sp.duration;
      improvement.golds.push(sp.key);
    }
  }

  // Personal bests, only for finished runs that qualify.
  if (run.won) {
    for (const cat of qualifyingCategories(run)) {
      const key = pbKey(cat, run.nice);
      const old = r.pbs[key];
      if (!old || run.ticks < old.ticks) {
        if (cat === "any" && old) improvement.savedVsPb = old.ticks - run.ticks;
        r.pbs[key] = {
          ticks: run.ticks,
          seed: run.seed,
          nice: run.nice,
          daily: run.daily,
          at: now,
          splits: run.splits,
        };
        improvement.personalBests.push(cat);
      }
    }
  }

  if (run.daily && opts.day) {
    // One entry per day. A practice attempt must never overwrite the
    // recorded one, so an existing entry for today wins.
    if (!r.daily.some((d) => d.day === opts.day)) {
      r.daily.unshift({ day: opts.day, ticks: run.ticks, won: run.won });
      r.daily = r.daily.slice(0, MAX_DAILY);
    }
  }

  r.recent.unshift({ ticks: run.ticks, won: run.won, nice: run.nice, at: now, seed: run.seed });
  r.recent = r.recent.slice(0, MAX_RECENT);

  return { records: r, improvement };
}

// ── Derived views ─────────────────────────────────────────────────────

/** The theoretical best run: every segment at its personal gold. Never
 *  achieved, always the target — the gap to your PB is the honest
 *  measure of how much time is still on the table. */
export function sumOfBest(r: Records, keys: readonly string[]): number | null {
  let total = 0;
  for (const k of keys) {
    const g = r.golds[k];
    if (g === undefined) return null; // incomplete — an estimate would mislead
    total += g;
  }
  return total;
}

/** Split-by-split comparison against a reference run. Positive delta is
 *  time lost. */
export function compareSplits(
  run: Split[],
  reference: Split[] | undefined,
): Array<{ split: Split; delta: number | null; gold: boolean }> {
  const byKey = new Map((reference ?? []).map((s) => [s.key, s]));
  return run.map((sp) => {
    const ref = byKey.get(sp.key);
    return {
      split: sp,
      delta: ref ? sp.at - ref.at : null,
      gold: false,
    };
  });
}

export const winRate = (lt: Lifetime): number => (lt.runs === 0 ? 0 : lt.wins / lt.runs);

/** Grazes per hit taken — the single best measure of whether someone has
 *  understood the game, since the whole economy runs on nerve. */
export const nerve = (lt: Lifetime): number | null =>
  lt.hitsTaken === 0 ? (lt.grazes > 0 ? Infinity : null) : lt.grazes / lt.hitsTaken;

/** Current consecutive-day streak on the daily, counting back from
 *  `today`. Days are ISO strings; a gap ends the streak. */
export function dailyStreak(entries: readonly DailyEntry[], today: string): number {
  const days = new Set(entries.map((e) => e.day));
  let streak = 0;
  const d = new Date(`${today}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return 0;
  for (;;) {
    const key = d.toISOString().slice(0, 10);
    if (!days.has(key)) break;
    streak += 1;
    d.setUTCDate(d.getUTCDate() - 1);
  }
  return streak;
}

export const formatTicks = (ticks: number): string => {
  const total = ticks / TICK_HZ;
  const m = Math.floor(total / 60);
  return `${m}:${(total - m * 60).toFixed(2).padStart(5, "0")}`;
};

/** Signed delta, the way a split timer shows it. */
export const formatDelta = (ticks: number): string =>
  `${ticks > 0 ? "+" : "-"}${formatTicks(Math.abs(ticks))}`;

// ── Persistence ───────────────────────────────────────────────────────

function validate(raw: unknown): Records | null {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as Partial<Records>;
  if (r.version !== RECORDS_VERSION) return null;
  const base = emptyRecords();
  // Merged field by field so a truncated or hand-edited blob degrades to
  // defaults instead of rendering NaN into every panel.
  return {
    ...base,
    ...r,
    version: RECORDS_VERSION,
    pbs: typeof r.pbs === "object" && r.pbs ? r.pbs : {},
    golds: typeof r.golds === "object" && r.golds ? r.golds : {},
    cards: typeof r.cards === "object" && r.cards ? r.cards : {},
    upgrades: typeof r.upgrades === "object" && r.upgrades ? r.upgrades : {},
    lifetime: { ...base.lifetime, ...(r.lifetime ?? {}) },
    daily: Array.isArray(r.daily) ? r.daily.slice(0, MAX_DAILY) : [],
    recent: Array.isArray(r.recent) ? r.recent.slice(0, MAX_RECENT) : [],
  };
}

export function loadRecords(storage: Storage, key = RECORDS_KEY): Records {
  try {
    const raw = storage.getItem(key);
    if (!raw) return emptyRecords();
    return validate(JSON.parse(raw)) ?? emptyRecords();
  } catch {
    return emptyRecords();
  }
}

export function saveRecords(r: Records, storage: Storage, key = RECORDS_KEY): void {
  try {
    storage.setItem(key, JSON.stringify(r));
  } catch {
    /* quota — records are a nicety, never a reason to break a run */
  }
}
